import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Home, Users, KanbanSquare, LayoutGrid, CheckSquare, Calendar, BarChart3, Settings, Plus, Menu, X, RotateCcw, LogOut } from "lucide-react";
import { Dashboard } from "./Dashboard";
import { Leads } from "./Leads";
import { Pipeline } from "./Pipeline";
import { Apartments } from "./Apartments";
import { Tasks } from "./Tasks";
import { CalendarView } from "./CalendarView";
import { Analytics } from "./Analytics";
import { SettingsView } from "./Settings";
import { LeadForm } from "./LeadForm";
import { Login } from "./Login";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAV = [
  { id: "inicio", label: "Inicio", icon: Home },
  { id: "leads", label: "Leads", icon: Users },
  { id: "pipeline", label: "Pipeline", icon: KanbanSquare },
  { id: "apartamentos", label: "Apartamentos", icon: LayoutGrid },
  { id: "tareas", label: "Tareas", icon: CheckSquare },
  { id: "calendario", label: "Calendario", icon: Calendar },
  { id: "analitica", label: "Analítica", icon: BarChart3 },
  { id: "config", label: "Configuración", icon: Settings },
];

const TITLES = {
  inicio: ["Inicio", "Resumen general del proyecto Jade Residence"],
  leads: ["Leads", "Gestiona y haz seguimiento de tus contactos"],
  pipeline: ["Pipeline", "Embudo de ventas · arrastra las tarjetas entre etapas"],
  apartamentos: ["Apartamentos", "18 unidades · €59.600 c/u"],
  tareas: ["Tareas", "Seguimientos y recordatorios"],
  calendario: ["Calendario", "Tareas y seguimientos programados"],
  analitica: ["Analítica", "Métricas clave del pipeline"],
  config: ["Configuración", "Datos de la empresa y copias de seguridad"],
};

export default function CrmApp() {
  const [auth, setAuth] = useState(null);
  const [data, setData] = useState(null);
  const [section, setSection] = useState("inicio");
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const call = useCallback(async (method, path, body) => {
    try {
      return await axios({ method, url: `${API}/crm${path}`, data: body, withCredentials: true });
    } catch (e) {
      if (e.response?.status === 401) setAuth(false);
      throw e;
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const r = await call("get", "/state");
      setData(r.data);
    } catch { /* 401 ya gestionado en call */ }
  }, [call]);

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true }).then((r) => setAuth(r.data)).catch(() => setAuth(false));
  }, []);

  useEffect(() => { if (auth) refresh(); }, [auth, refresh]);

  useEffect(() => {
    if (!auth) return;
    const t = setInterval(() => { if (document.visibilityState === "visible") refresh(); }, 30000);
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [auth, refresh]);

  if (auth === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#101f18]">
        <p data-testid="crm-loading" className="animate-pulse text-sm text-white/60">Cargando CRM...</p>
      </div>
    );
  }
  if (auth === false) return <Login onSuccess={setAuth} />;
  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#101f18]">
        <p data-testid="crm-data-loading" className="animate-pulse text-sm text-white/60">Cargando datos del equipo...</p>
      </div>
    );
  }

  const upsertLead = async (lead) => {
    setData((d) => ({ ...d, leads: d.leads.some((l) => l.id === lead.id) ? d.leads.map((l) => (l.id === lead.id ? lead : l)) : [...d.leads, lead] }));
    await call("put", `/leads/${lead.id}`, lead);
  };
  const removeLead = async (id) => {
    setData((d) => ({ ...d, leads: d.leads.filter((l) => l.id !== id) }));
    await call("delete", `/leads/${id}`);
  };
  const upsertApto = async (a) => {
    setData((d) => ({ ...d, apartments: d.apartments.map((x) => (x.id === a.id ? a : x)) }));
    await call("put", `/apartments/${a.id}`, a);
  };
  const upsertTask = async (t) => {
    setData((d) => ({ ...d, tasks: d.tasks.some((x) => x.id === t.id) ? d.tasks.map((x) => (x.id === t.id ? t : x)) : [...d.tasks, t] }));
    await call("put", `/tasks/${t.id}`, t);
  };
  const removeTask = async (id) => {
    setData((d) => ({ ...d, tasks: d.tasks.filter((x) => x.id !== id) }));
    await call("delete", `/tasks/${id}`);
  };
  const saveConfig = async (config) => {
    setData((d) => ({ ...d, config }));
    await call("put", "/config", config);
  };
  const importAll = async (payload) => { await call("post", "/import", payload); await refresh(); };
  const wipeAll = async () => { await call("post", "/wipe"); await refresh(); };
  const resetAll = async () => { await call("post", "/reset"); await refresh(); };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } finally {
      setAuth(false);
      setData(null);
    }
  };

  const addLeadComment = async (leadId, texto) => {
    const r = await call("post", `/leads/${leadId}/comments`, { texto });
    setData((d) => ({
      ...d,
      leads: d.leads.map((l) => l.id === leadId ? { ...l, comentarios: [...(l.comentarios || []), r.data.comentario] } : l)
    }));
  };

  const ctx = {
    data, user: auth, go: setSection, openNewLead: () => setShowLeadForm(true),
    upsertLead, removeLead, upsertApto, upsertTask, removeTask, saveConfig, importAll, wipeAll, addLeadComment,
  };

  const reset = () => {
    if (window.confirm("¿Reiniciar todos los datos y volver a los datos de ejemplo? Afecta a todo el equipo.")) resetAll();
  };

  const nav = (mobile) => (
    <>
      <div className="flex items-center gap-3 px-5 pt-6">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#3a7a50] text-lg font-bold text-white">T</div>
        <div>
          <p className="text-sm font-semibold text-white">{data.config.nombre} CRM</p>
          <p className="text-[11px] text-white/50">Jade Residence · Lombok</p>
        </div>
      </div>
      <nav className="mt-8 flex-1 space-y-1 px-3">
        {NAV.map((n) => {
          const Icon = n.icon;
          const active = section === n.id;
          return (
            <button
              key={n.id}
              data-testid={`crm-nav-${n.id}`}
              onClick={() => { setSection(n.id); if (mobile) setMenuOpen(false); }}
              className={`flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-sm transition-colors ${
                active ? "bg-[#2d5a3d] font-medium text-white" : "text-white/60 hover:bg-white/5 hover:text-white"
              }`}
            >
              <Icon className="h-[18px] w-[18px]" />
              {n.label}
            </button>
          );
        })}
      </nav>
      <div className="px-4 pb-6">
        <button
          data-testid="crm-nuevo-lead-btn"
          onClick={() => { setShowLeadForm(true); if (mobile) setMenuOpen(false); }}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#2d5a3d] px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#3a7a50]"
        >
          <Plus className="h-4 w-4" /> Nuevo lead
        </button>
        <p className="mt-4 text-center text-[11px] text-white/40">Datos compartidos en equipo</p>
      </div>
    </>
  );

  const [t, sub] = TITLES[section];

  return (
    <div className="flex min-h-screen bg-[#f5f0e8]/60 font-sans text-gray-900">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col bg-[#101f18] lg:flex">{nav(false)}</aside>

      {menuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMenuOpen(false)} />
          <aside className="absolute inset-y-0 left-0 flex w-72 flex-col bg-[#101f18]">
            <button data-testid="crm-menu-close" onClick={() => setMenuOpen(false)} className="absolute right-3 top-4 text-white/60" aria-label="Cerrar menú">
              <X className="h-5 w-5" />
            </button>
            {nav(true)}
          </aside>
        </div>
      )}

      <div className="flex min-h-screen flex-1 flex-col lg:pl-64">
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-gray-200/70 bg-white/90 px-4 py-3.5 backdrop-blur sm:px-8">
          <div className="flex items-center gap-3">
            <button data-testid="crm-menu-btn" onClick={() => setMenuOpen(true)} className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 lg:hidden" aria-label="Abrir menú">
              <Menu className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-lg font-bold text-gray-900 sm:text-xl">{t}</h1>
              <p className="hidden text-xs text-gray-500 sm:block">{sub}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <span data-testid="crm-user-name" className="mr-1 hidden rounded-full bg-[#2d5a3d]/10 px-3 py-1.5 text-xs font-medium text-[#2d5a3d] sm:inline-block">
              {auth.name}
            </span>
            <button data-testid="crm-reset-btn" onClick={reset} className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800">
              <RotateCcw className="h-3.5 w-3.5" /> Reiniciar datos
            </button>
            <button data-testid="crm-logout-btn" onClick={logout} className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600">
              <LogOut className="h-3.5 w-3.5" /> Salir
            </button>
          </div>
        </header>

        <main className="flex-1 px-4 py-6 sm:px-8" data-testid={`crm-section-${section}`}>
          {section === "inicio" && <Dashboard {...ctx} />}
          {section === "leads" && <Leads {...ctx} />}
          {section === "pipeline" && <Pipeline {...ctx} />}
          {section === "apartamentos" && <Apartments {...ctx} />}
          {section === "tareas" && <Tasks {...ctx} />}
          {section === "calendario" && <CalendarView {...ctx} />}
          {section === "analitica" && <Analytics {...ctx} />}
          {section === "config" && <SettingsView {...ctx} />}
        </main>
      </div>

      {showLeadForm && (
        <LeadForm
          onClose={() => setShowLeadForm(false)}
          onSave={(lead) => {
            upsertLead(lead);
            setShowLeadForm(false);
          }}
        />
      )}
    </div>
  );
}
